// Brand registry. This file holds only identity (display name, URL slug, how
// to recognise the brand in catalog data, which PlazaStack collection mirrors
// it). It holds NO counts: every count comes from the live catalog
// (lib/catalog.ts -> brandCounts), so it can never drift from what is for sale.
//
// Slugs are the long-standing /brands/<slug> URLs and are kept as they were.
// Two of them differ from the PlazaStack collection slugs (`sand-fog` vs
// `sandfog`, `d-g` vs `untitled-dg`); `collection` maps them.

export type BrandEntry = {
  /** Display name. */
  name:        string;
  /** /brands/<slug>. */
  slug:        string;
  /** The value data/catalog.json (and PlazaStack `brand`) uses, matched case-insensitively. */
  key:         string;
  /** PlazaStack collection slug that mirrors this brand, when one exists. */
  collection?: string;
};

const b = (name: string, slug: string, key = name, collection?: string): BrandEntry => ({ name, slug, key, collection });

// Order = display order on /brands and in the mega menu (top 12 first).
export const BRANDS: BrandEntry[] = [
  b('Lattafa',            'lattafa',            'Lattafa', 'lattafa'),
  b('BSB',                'bsb',                'BSB', 'bsb'),
  b('Sand+Fog',           'sand-fog',           'Sand+Fog', 'sandfog'),
  b("Victoria's Secret",  'victoria-secret',    'Victoria Secret', 'victoria-secret'),
  b('Jo Milano',          'jo-milano',          'Jo Milano', 'jo-milano'),
  b('Dolce & Gabbana',    'd-g',                'D&G', 'untitled-dg'),
  b('Ulta',               'ulta',               'Ulta', 'ulta'),
  b('Gulf Orchid',        'gulf-orchid',        'Gulf Orchid', 'gulf-orchid'),
  b('Ahuja',              'ahuja',              'Ahuja', 'ahuja'),
  b('YSL',                'ysl',                'YSL', 'ysl'),
  b('Valentino DBIR',    'valentino-dbir', 'Valentino DBIR', 'valentino-dbir'),
  b('Lancome',            'lancome',            'Lancome', 'lancome'),
  b('Phlur',              'phlur'),
  b('Khadlaj',            'khadlaj'),
  b('Jean Paul Gaultier', 'jean-paul-gaultier'),
  b('Ariana Grande',      'ariana-grande'),
  b('Armaf',              'armaf'),
  b('Ellis Brooklyn',     'ellis-brooklyn'),
  b('Khloe',              'khloe'),
  b('Bond No 9',          'bond-no-9'),
  b('Carolina Herrera',   'carolina-herrera'),
  b('Billie Eilish',      'billie-eilish'),
  b('Paris Corner',       'paris-corner'),
  b('Marc Jacobs',        'marc-jacobs'),
  b('Maison Alhambra',    'maison-alhambra'),
  b('Michael Kors',       'michael-kors'),
  b('Vince Camuto',       'vince-camuto'),
  b('Viktor & Rolfe',     'viktor-rolfe'),
  b('Xerjoff',            'xerjoff'),
  b('JHAG',               'jhag'),
  b('Armani',             'armani'),
  b('Jimmy Choo',         'jimmy-choo'),
  b('Kayali',             'kayali'),
  b('Michael Malul',      'michael-malul'),
  b('Montblanc',          'mont-blanc',         'MontBlanc'),
  b('Montale',            'montale'),
  b('Moschino',           'moschino'),
  b('Mugler',             'mugler'),
  b('Parfums de Marlay',  'parfums-de-marlay'),
  b('Rayhaan',            'rayhaan'),
  b('Sex in The City',    'sex-in-the-city'),
  b('Sol de Janeiro',     'sol-de-janeiro'),
  b('Supersuite',         'supersuite'),
  b('Truly',              'truly'),
  b('Vera Wang',          'vera-wang'),
];

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9&+]+/g, ' ').trim();

const BY_SLUG = new Map(BRANDS.map((x) => [x.slug, x]));
const BY_KEY  = new Map<string, BrandEntry>();
for (const x of BRANDS) {
  BY_KEY.set(norm(x.key), x);
  BY_KEY.set(norm(x.name), x);
}
// Spellings seen in PlazaStack `brand` / descriptions that are not the catalog key.
BY_KEY.set(norm("Victoria's Secret"), BY_SLUG.get('victoria-secret')!);
BY_KEY.set(norm('Dolce & Gabbana'), BY_SLUG.get('d-g')!);
BY_KEY.set(norm('Dolce and Gabbana'), BY_SLUG.get('d-g')!);
BY_KEY.set(norm('Sand+Fog'), BY_SLUG.get('sand-fog')!);
BY_KEY.set(norm('Sand and Fog'), BY_SLUG.get('sand-fog')!);

export const TOP_BRANDS = BRANDS.slice(0, 12);

export function brandBySlug(slug: string): BrandEntry | undefined {
  return BY_SLUG.get(slug);
}

/** Recognise a brand from any of its spellings. */
export function brandByName(name: string | null | undefined): BrandEntry | undefined {
  if (!name) return undefined;
  return BY_KEY.get(norm(name));
}

/** Brand whose PlazaStack collection slug is `collection`. */
export function brandByCollection(collection: string): BrandEntry | undefined {
  return BRANDS.find((x) => x.collection === collection);
}

export function slugify(s: string): string {
  return s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
